import { useEffect, useRef } from 'react';

import { useAtomValue } from 'jotai';

import { Playback3DScrubber } from '@blocks';
import ElvaVisualizer, {
    ElvaVisualizerMeasurements,
    ElvaVisualizerProvider,
    useElvaVisualizerContext,
} from '@elva-visualizer';
import { activeData3dAtom, syncedFrameAtom } from '@store';

import { VIEW_PRESET_CONFIG } from '../hooks/useViewPresetCamera';
import { Box } from '@ui';

export const VIEW_PRESETS = {
    FACE_ON: 'face_on',
    DOWN_THE_LINE: 'down_the_line',
    TOP: 'top',
    BACKSIDE: 'backside',
} as const;

export type ViewPreset = (typeof VIEW_PRESETS)[keyof typeof VIEW_PRESETS];

const CAMERA_UNLOCK_DELAY_MS = 1000;
const BASE_VERTICAL_OFFSET = 0.06;

type CameraPresetApplierProps = {
    preset: ViewPreset | null;
    onPresetApplied: () => void;
};

function CameraPresetApplier({ preset, onPresetApplied }: CameraPresetApplierProps) {
    const { setForcedAzimuthalAngle, setForcedPolarAngle } = useElvaVisualizerContext();
    const unlockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!preset) return;

        if (unlockTimeoutRef.current) {
            clearTimeout(unlockTimeoutRef.current);
        }

        const config = VIEW_PRESET_CONFIG[preset];
        if (config) {
            setForcedAzimuthalAngle(config.azimuthal);
            setForcedPolarAngle(config.polar);

            unlockTimeoutRef.current = setTimeout(() => {
                setForcedAzimuthalAngle(null);
                setForcedPolarAngle(null);
                onPresetApplied();
            }, CAMERA_UNLOCK_DELAY_MS);
        }

        return () => {
            if (unlockTimeoutRef.current) {
                clearTimeout(unlockTimeoutRef.current);
            }
        };
    }, [preset, setForcedAzimuthalAngle, setForcedPolarAngle, onPresetApplied]);

    return null;
}

type VisualizerSectionProps = {
    freezePosition: string | null;
    effectiveCameraFocus: string | null;
    effectiveCameraFocusFrame: number | undefined;
    effectiveHorizontalOffset: number;
    pendingPreset: ViewPreset | null;
    onPresetApplied: () => void;
    onCameraInteraction: () => void;
    onFreezePositionChange: (position: string | null) => void;
    overlayComparison: boolean;
};

export function VisualizerSection({
    freezePosition,
    effectiveCameraFocus,
    effectiveCameraFocusFrame,
    effectiveHorizontalOffset,
    pendingPreset,
    onPresetApplied,
    onCameraInteraction,
    onFreezePositionChange,
    overlayComparison,
}: VisualizerSectionProps) {
    const syncedFrame = useAtomValue(syncedFrameAtom);
    const activeData = useAtomValue(activeData3dAtom);

    if (!activeData) return null;

    return (
        <ElvaVisualizerProvider>
            <CameraPresetApplier preset={pendingPreset} onPresetApplied={onPresetApplied} />
            <Box position="absolute" right={0} top={64}>
                <ElvaVisualizerMeasurements />
            </Box>
            <ElvaVisualizer
                freezePosition={freezePosition}
                fallbackFocus={effectiveCameraFocus}
                fallbackFocusFrame={effectiveCameraFocusFrame}
                onCameraInteraction={onCameraInteraction}
                verticalOffset={BASE_VERTICAL_OFFSET}
                horizontalOffset={effectiveHorizontalOffset}
                comparisonFrame={syncedFrame}
                overlayComparison={overlayComparison}
            />
            <Playback3DScrubber freezePosition={freezePosition} onFreezePositionChange={onFreezePositionChange} />
        </ElvaVisualizerProvider>
    );
}
